/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
    Box,
    Typography,
    Button,
    Stack,
    Collapse,
    CircularProgress,
    Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { error as errorPalette, primary, warning } from 'src/theme/palette';

// -----------------------------------------------

function parseDurationSecs(d) {
    if (!d) return null;
    if (typeof d === 'number') return d;
    if (typeof d !== 'string') return null;
    const parts = d.replace(' ', ':').split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 4) return parts[0] * 86400 + parts[1] * 3600 + parts[2] * 60 + parts[3];
    return null;
}

function formatSeconds(totalSeconds) {
    if (!totalSeconds || totalSeconds < 0) return '00:00:00';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

function formatEntry(entry) {
    const start = entry.started_at
        ? new Date(entry.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '—';
    const end = entry.stopped_at
        ? new Date(entry.stopped_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'running';
    const dur = entry.duration_seconds ? formatSeconds(entry.duration_seconds) : '—';
    return `${start} → ${end} (${dur})`;
}

// -----------------------------------------------

export default function TaskTimeTracker({ practiceId, taskId, task, onRefresh }) {
    const [timerLoading, setTimerLoading] = useState(false);
    const [liveSeconds, setLiveSeconds] = useState(0);
    const [entriesOpen, setEntriesOpen] = useState(false);
    const intervalRef = useRef(null);

    const timeEntries = task?.time_entries || [];
    const totalSeconds = task?.total_time_seconds || 0;

    // Detect active timer: last entry with no stopped_at
    const activeEntry = timeEntries.find((e) => e.started_at && !e.stopped_at)
        || (task?.active_timer_started_at ? { started_at: task.active_timer_started_at } : null);
    const isRunning = Boolean(activeEntry);

    // Live tick when running
    useEffect(() => {
        if (isRunning && activeEntry?.started_at) {
            const startMs = new Date(activeEntry.started_at).getTime();
            const tick = () => {
                const elapsed = Math.floor((Date.now() - startMs) / 1000);
                setLiveSeconds(elapsed);
            };
            tick();
            intervalRef.current = setInterval(tick, 1000);
        } else {
            setLiveSeconds(0);
            if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [isRunning, activeEntry?.started_at]);

    const handleStart = async () => {
        setTimerLoading(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/start-timer/`,
                'post',
                {}
            );
            if (res.status === 200 || res.status === 201) {
                if (onRefresh) onRefresh();
            } else {
                console.warn('[TaskTimeTracker] Start timer failed:', res.status, res.data);
                const msg = res.data?.detail || 'Failed to start timer.';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_timer_start' } });
            dispatch(updateError({ success: false, message: 'Failed to start timer.' }));
        } finally {
            setTimerLoading(false);
        }
    };

    const handleStop = async () => {
        setTimerLoading(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/stop-timer/`,
                'post',
                {}
            );
            if (res.status === 200 || res.status === 201) {
                if (onRefresh) onRefresh();
            } else {
                console.warn('[TaskTimeTracker] Stop timer failed:', res.status, res.data);
                const msg = res.data?.detail || 'Failed to stop timer.';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_timer_stop' } });
            dispatch(updateError({ success: false, message: 'Failed to stop timer.' }));
        } finally {
            setTimerLoading(false);
        }
    };

    const completedEntries = timeEntries.filter((e) => e.stopped_at);
    const displaySeconds = isRunning ? totalSeconds + liveSeconds : totalSeconds;

    const targetSecs = parseDurationSecs(task?.target_duration);
    let slaState = null;
    if (targetSecs) {
        if (displaySeconds >= targetSecs) slaState = 'exceeded';
        else if (displaySeconds >= targetSecs * 0.8) slaState = 'near';
        else slaState = 'on_track';
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>
                {/* Clock icon */}
                <Iconify
                    icon="eva:clock-fill"
                    width={16}
                    sx={{ color: isRunning ? errorPalette.main : 'text.disabled', flexShrink: 0 }}
                />

                {/* Time display */}
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        color: isRunning ? errorPalette.main : 'text.primary',
                        minWidth: 72,
                    }}
                >
                    {formatSeconds(displaySeconds)}
                </Typography>

                {/* SLA chip */}
                {slaState === 'exceeded' && (
                    <Chip
                        size="small"
                        label="SLA exceeded"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, borderRadius: 1, color: errorPalette.main, bgcolor: alpha(errorPalette.main, 0.08) }}
                    />
                )}
                {slaState === 'near' && (
                    <Chip
                        size="small"
                        label="Near limit"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, borderRadius: 1, color: warning.main, bgcolor: alpha(warning.main, 0.08) }}
                    />
                )}
                {slaState === 'on_track' && (
                    <Chip
                        size="small"
                        label="On track"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, borderRadius: 1, color: primary.main, bgcolor: alpha(primary.main, 0.08) }}
                    />
                )}

                {/* Start / Stop button */}
                {isRunning ? (
                    <Button
                        size="small"
                        variant="contained"
                        disabled={timerLoading}
                        onClick={handleStop}
                        startIcon={timerLoading ? <CircularProgress size={12} color="inherit" /> : <Iconify icon="eva:stop-circle-fill" width={14} />}
                        sx={{
                            bgcolor: errorPalette.main,
                            '&:hover': { bgcolor: errorPalette.dark },
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5,
                            fontWeight: 700,
                        }}
                    >
                        {timerLoading ? 'Stopping…' : 'Stop'}
                    </Button>
                ) : (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={timerLoading}
                        onClick={handleStart}
                        startIcon={timerLoading ? <CircularProgress size={12} color="inherit" /> : <Iconify icon="eva:play-circle-fill" width={14} />}
                        sx={{
                            borderColor: primary.main,
                            color: primary.main,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5,
                            fontWeight: 600,
                            '&:hover': { bgcolor: alpha(primary.main, 0.06) },
                        }}
                    >
                        {timerLoading ? 'Starting…' : 'Start timer'}
                    </Button>
                )}

                {/* View entries toggle */}
                {completedEntries.length > 0 && (
                    <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() => setEntriesOpen(!entriesOpen)}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', p: 0 }}
                    >
                        {entriesOpen ? 'Hide entries' : `View entries (${completedEntries.length})`}
                    </Button>
                )}
            </Stack>

            {/* Entry list */}
            <Collapse in={entriesOpen && completedEntries.length > 0}>
                <Box sx={{ mt: 1, pl: 3.5 }}>
                    {completedEntries.map((entry, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mb: 0.25 }}>
                            {formatEntry(entry)}
                        </Typography>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

TaskTimeTracker.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    task: PropTypes.shape({
        total_time_seconds: PropTypes.number,
        time_entries: PropTypes.arrayOf(PropTypes.shape({
            started_at: PropTypes.string,
            stopped_at: PropTypes.string,
            duration_seconds: PropTypes.number,
        })),
        active_timer_started_at: PropTypes.string,
        target_duration: PropTypes.string,
    }),
    onRefresh: PropTypes.func,
};

TaskTimeTracker.defaultProps = {
    task: null,
    onRefresh: null,
};
